import React, { useEffect, useRef, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase"; 
import logoimg from '../../assets/logo.svg'

import styles from "./FoodGalleryCarousell.module.css";

const FoodGalleryCarousell = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [index, setIndex] = useState(0);
  const [visibleSlides, setVisibleSlides] = useState(4);
  const intervalRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchImages = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "food-gallery"));
        const imageUrls = querySnapshot.docs.map((doc) => doc.data().imageUrl); 
        const validUrls = imageUrls.filter(url => url);
        setImages([...validUrls, ...validUrls]); 
      } catch (error) {
        console.error("Error fetching gallery images: ", error);
      } finally {
        setLoading(false);
      }
    };
    fetchImages();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 600) setVisibleSlides(1);
      else if (window.innerWidth < 900) setVisibleSlides(2);
      else if (window.innerWidth < 1200) setVisibleSlides(3);
      else setVisibleSlides(4);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const maxIndex = images.length > 0 ? images.length - visibleSlides : 0;

  const nextSlide = useCallback(() => {
    setIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  }, [maxIndex]);

  const prevSlide = useCallback(() => {
    setIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));
  }, [maxIndex]);

  const stopAuto = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
  }, []);

  const startAuto = useCallback(() => {
    stopAuto();
    intervalRef.current = setInterval(() => {
      nextSlide();
    }, 3000);
  }, [nextSlide, stopAuto]);

  useEffect(() => {
    if (images.length === 0) return;
    startAuto();
    return stopAuto;
  }, [startAuto, stopAuto, images.length]);

  const handleNavigate = () => navigate('/gallery');

  if (loading) return <div className={styles.loader}>Loading Gallery...</div>;
  if (images.length === 0) return null;

  return (
    <section className={styles.gallerySection}>
      <motion.div
        className={styles.galleryHeader}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        <div className={styles.titleGroup}>
          <h2>
            The Essence of<br />
            <img src={logoimg} alt="Baglami" className={styles.baglamiLogo} /> Captured
          </h2>
        </div>
        <motion.a
          whileHover={{ x: 5 }}
          className={styles.viewGallery}
          onClick={handleNavigate}
          style={{ cursor: 'pointer' }}
        >
          View Gallery ↗
        </motion.a>
      </motion.div>

      <div
        className={styles.carouselWrapper}
        onMouseEnter={stopAuto}
        onMouseLeave={startAuto}
      >
        <button className={`${styles.navBtn} ${styles.prev}`} onClick={prevSlide} aria-label="Previous">
          ←
        </button>

        <div className={styles.carouselViewport}>
          <motion.div
            className={styles.carouselTrack}
            animate={{ x: `-${index * (100 / visibleSlides)}%` }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
          >
            {images.map((img, i) => (
              <motion.div
                key={i}
                className={styles.slide}
                style={{ width: `${100 / visibleSlides}%` }}
                whileHover={{ y: -10 }}
              >
                <div className={styles.imageContainer}>
                  <img src={img} alt={`Gallery item ${i}`} loading="lazy" />
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>

        <button className={`${styles.navBtn} ${styles.next}`} onClick={nextSlide} aria-label="Next">
          →
        </button>
      </div>

      <div className={styles.dots}>
        {Array.from({ length: Math.min(maxIndex + 1, 10) }).map((_, i) => (
          <motion.span
            key={i}
            initial={false}
            animate={{
              scale: i === index ? 1.5 : 1,
              backgroundColor: i === index ? "#e25822" : "#ccc"
            }}
            onClick={() => setIndex(i)}
          ></motion.span>
        ))}
      </div>
    </section>
  );
};

export default FoodGalleryCarousell;